import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { MoodPicker } from './MoodPicker';

/**
 * Writing something new. Opens already holding a prompt when they arrived from an exercise
 * or supplement ("Reflect on this"), otherwise offers openers in the program's own language.
 */
export const JournalComposer = ({ moods, seed, onSaved, onClearSeed }) => {
  const [prompts, setPrompts] = useState([]);
  const [earned, setEarned] = useState([]);
  const [showAllEarned, setShowAllEarned] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [mood, setMood] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/companion/prompts`)
      .then((r) => { setPrompts(r.data.prompts || []); setEarned(r.data.earned || []); })
      .catch(() => { setPrompts([]); setEarned([]); });
  }, []);

  useEffect(() => {
    if (seed?.prompt) setPrompt(seed.prompt);
    if (seed?.title) setTitle(seed.title);
  }, [seed]);

  const save = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/companion/journal`, {
        title: title.trim(),
        body: body.trim(),
        mood,
        prompt_text: prompt,
        content_id: seed?.contentId || null,
        source: prompt ? 'prompt' : 'free',
      });
      setTitle(''); setBody(''); setMood(null); setPrompt('');
      onClearSeed?.();
      toast.success('Kept in your journal');
      onSaved?.();
    } catch {
      toast.error('That entry was not saved');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4"
         data-testid="journal-composer">
      {seed?.contentTitle && (
        <div className="flex items-start gap-2 rounded-lg border border-cyan-300/25
                        bg-cyan-400/[0.07] px-3 py-2" data-testid="journal-seed">
          <Sparkles className="w-3.5 h-3.5 text-cyan-300 mt-0.5 shrink-0" />
          <p className="text-xs text-cyan-100/90">
            Reflecting on <span className="text-white">{seed.contentTitle}</span>
          </p>
        </div>
      )}

      {prompt ? (
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm text-white/80 italic" data-testid="journal-active-prompt">
            {prompt}
          </p>
          <button
            onClick={() => { setPrompt(''); onClearSeed?.(); }}
            className="text-[11px] text-white/40 hover:text-white/70 shrink-0
                       transition-colors duration-200"
            data-testid="journal-clear-prompt"
          >
            clear
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="journal-prompt-chips">
            {prompts.slice(0, 5).map((p) => (
              <button
                key={p.id}
                onClick={() => setPrompt(p.text)}
                className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5
                           text-[11px] text-white/60 hover:text-white hover:bg-white/10
                           transition-colors duration-200"
                data-testid={`journal-prompt-${p.id}`}
              >
                {p.text}
              </button>
            ))}
          </div>

          {/* Kept from worksheets they have already done — theirs from here on. */}
          {earned.length > 0 && (
            <div className="space-y-2 pt-1 border-t border-white/[0.06]"
                 data-testid="journal-earned-prompts">
              <p className="text-[10px] uppercase tracking-wider text-white/30">From your work</p>
              <div className="flex flex-wrap gap-2">
                {(showAllEarned ? earned : earned.slice(0, 5)).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPrompt(p.text)}
                    className="rounded-full border border-cyan-300/20 bg-cyan-400/[0.06] px-3 py-1.5
                               text-[11px] text-cyan-100/70 hover:text-white hover:bg-cyan-400/15
                               transition-colors duration-200"
                    title={p.from ? `From ${p.from}` : undefined}
                    data-testid={`journal-earned-prompt-${p.id}`}
                  >
                    {p.text}
                  </button>
                ))}
                {earned.length > 5 && (
                  <button
                    onClick={() => setShowAllEarned((v) => !v)}
                    className="rounded-full px-3 py-1.5 text-[11px] text-white/40
                               hover:text-white/80 transition-colors duration-200"
                    data-testid="journal-earned-toggle"
                  >
                    {showAllEarned ? 'show fewer' : `show all ${earned.length}`}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="A title, if you want one"
        className="w-full bg-transparent border-b border-white/10 pb-2 text-base text-white
                   placeholder-white/25 focus:outline-none focus:border-cyan-400/60
                   transition-colors duration-200"
        data-testid="journal-title-input"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={7}
        placeholder="Write as much or as little as you like. This is yours."
        className="w-full bg-black/25 border border-white/10 rounded-xl px-4 py-3 text-sm
                   text-white placeholder-white/25 leading-relaxed focus:outline-none
                   focus:border-cyan-400/60 transition-colors duration-200"
        data-testid="journal-body-input"
      />

      <div data-testid="journal-entry-mood">
        <span className="block text-xs uppercase tracking-wider text-white/35 mb-3">
          Where you are, writing this — optional
        </span>
        <MoodPicker
          moods={moods}
          latest={mood}
          onPick={(m) => setMood(mood === m ? null : m)}
          compact
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={busy || !body.trim()}
          className="rounded-full bg-cyan-400/20 border border-cyan-300/40 px-5 py-2 text-sm
                     text-cyan-50 hover:bg-cyan-400/30 disabled:opacity-40
                     transition-colors duration-200"
          data-testid="journal-save-btn"
        >
          {busy ? 'Keeping…' : 'Keep this'}
        </button>
      </div>
    </div>
  );
};

export default JournalComposer;
